import React, { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import './AdminPage.css'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
const getToken = () => localStorage.getItem('adminToken') || window.__SESSION_TOKEN__ || sessionStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('authToken')

const request = async (path, options = {}) => {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  })
  const result = await response.json()
  if (!response.ok) throw new Error(result.error || 'Request failed')
  return result
}

function AdminPage({ onLogout }) {
  const [access, setAccess] = useState('checking')
  const [tickets, setTickets] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [selected, setSelected] = useState(null)
  const [mobileDetail, setMobileDetail] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [channel, setChannel] = useState('')
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSendingReply, setIsSendingReply] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  const loadTickets = async () => {
    try {
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(status && { status }),
        ...(channel && { channel }),
      })
      const result = await request(`/admin/tickets?${params}`)
      setTickets(result.tickets)
      if (!selectedId && result.tickets[0]) setSelectedId(result.tickets[0].TicketId)
      setError('')
    } catch (loadError) {
      setError(loadError.message)
    }
  }

  const loadSelected = async (ticketId) => {
    if (!ticketId) return
    try {
      setSelected((await request(`/admin/tickets/${ticketId}`)).ticket)
    } catch (loadError) {
      setError(loadError.message)
    }
  }

  const openTicket = (ticketId) => {
    setSelectedId(ticketId)
    setMobileDetail(true)
  }

  const navigateTicket = (direction) => {
    const index = tickets.findIndex((ticket) => ticket.TicketId === selectedId)
    const nextTicket = tickets[index + direction]
    if (nextTicket) openTicket(nextTicket.TicketId)
  }

  useEffect(() => {
    request('/admin/session')
      .then(() => {
        setAccess('allowed')
      })
      .catch((authError) => setAccess(authError.message.includes('Admin') ? 'denied' : 'error'))
  }, [])

  useEffect(() => {
    if (access === 'allowed') loadTickets()
  }, [access, search, status, channel])
  useEffect(() => {
    loadSelected(selectedId)
  }, [selectedId])

  const metrics = useMemo(
    () => ({
      open: tickets.filter((ticket) => ticket.Status !== 'Resolved').length,
      urgent: tickets.filter((ticket) => ticket.Status === 'New').length,
      channels: new Set(tickets.map((ticket) => ticket.Channel)).size,
    }),
    [tickets],
  )

  const updateStatus = async (nextStatus) => {
    if (!selected || !nextStatus || nextStatus === selected.Status || isUpdatingStatus) return

    setIsUpdatingStatus(true)
    try {
      await request(`/admin/tickets/${selected.TicketId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      })
      setError('')
      await loadSelected(selected.TicketId)
      await loadTickets()
    } catch (statusError) {
      setError(statusError.message)
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const submitComment = async (event) => {
    event.preventDefault()
    if (!comment.trim() || isSendingReply) return

    setIsSendingReply(true)
    try {
      await request(`/admin/tickets/${selected.TicketId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body: comment }),
      })
      setComment('')
      setError('')
      await loadSelected(selected.TicketId)
      await loadTickets()
    } catch (commentError) {
      setError(commentError.message)
      await loadSelected(selected.TicketId)
      await loadTickets()
    } finally {
      setIsSendingReply(false)
    }
  }

  const startFreshTicket = async () => {
    const result = await request(`/admin/tickets/${selected.TicketId}/fresh`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
    setSelectedId(result.ticket.TicketId)
    await loadTickets()
  }

  const getReplyChannel = (ticket) => {
    if (!ticket) return 'email'
    if (ticket.Channel === 'whatsapp' && ticket.CustomerPhone) return 'WhatsApp'
    if (ticket.CustomerEmail) return 'email'
    return ticket.CustomerPhone ? 'WhatsApp' : 'email'
  }

  const formatDeliveryChannel = (channel) => (channel === 'whatsapp' ? 'WhatsApp' : channel || 'email')

  const conversationRef = React.useRef(null)

  useEffect(() => {
    document.body.classList.add('admin-page')
    return () => document.body.classList.remove('admin-page')
  }, [])

  useEffect(() => {
    if (!conversationRef.current) return
    conversationRef.current.scrollTop = conversationRef.current.scrollHeight
  }, [selectedId, selected?.comments?.length])

  const refreshNow = async () => {
    if (isRefreshing) return

    setIsRefreshing(true)
    try {
      await loadTickets()
      if (selectedId) await loadSelected(selectedId)
    } finally {
      setIsRefreshing(false)
    }
  }

  if (access === 'checking') return <div className="admin-state">Verifying admin access...</div>
  if (access === 'denied') return <Navigate to="/" replace state={{ adminError: 'Admin access required.' }} />
  if (access === 'error')
    return (
      <div className="admin-state">
        <h1>Admin unavailable</h1>
        <p>{error || 'Could not verify your session.'}</p>
      </div>
    )

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-mark">
          <span className="material-icons">bolt</span>
          <span>
            MORECHARZ
            <br />
            <b>ADMIN</b>
          </span>
        </div>
        <nav>
          <a className="active" href="#supportcenter">
            <span className="material-icons">support_agent</span>Support Center <strong>{metrics.open}</strong>
          </a>
          <a href="#reports">
            <span className="material-icons">bar_chart</span>Reports
          </a>
          <a href="#sales">
            <span className="material-icons">trending_up</span>Sales analysis
          </a>
        </nav>
        <div className="sidebar-foot">
          <span className="material-icons">admin_panel_settings</span>Admin workspace
          {onLogout && <button type="button" className="admin-logout" onClick={onLogout}><span className="material-icons">logout</span>Log out</button>}
        </div>
      </aside>
      <main className="admin-main" id="supportcenter">
        <header className="admin-header">
          <div className="admin-header-copy">
            <p className="eyebrow">CUSTOMER OPERATIONS / SUPPORT CENTER</p>
            <h1>MoRecharz Support Center</h1>
            <p className="admin-subtitle">One queue for every conversation, with context that travels with the ticket.</p>
          </div>
          <div className="admin-header-tools">
            <div className="admin-metrics">
              <div className="metric-card metric-open">
                <span>Open</span>
                <b>{metrics.open}</b>
                <small>tickets</small>
              </div>
              <div className="metric-card metric-new">
                <span>New</span>
                <b>{metrics.urgent}</b>
                <small>arrivals</small>
              </div>
            </div>
            <div className="admin-date">
              <button
                type="button"
                className={`icon-button ${isRefreshing ? 'is-refreshing' : ''}`}
                onClick={refreshNow}
                aria-label="Refresh queue"
                title="Refresh queue"
                disabled={isRefreshing}
              >
                <span className="material-icons">{isRefreshing ? 'autorenew' : 'refresh'}</span>
              </button>
            </div>
          </div>
        </header>
        <section className="admin-workspace">
          <div className={`ticket-column ${mobileDetail ? 'mobile-hidden' : ''}`}>
            <div className="queue-heading">
              <div>
                <span className="eyebrow">ALL CONVERSATIONS</span>
                <h2>Ticket Queue</h2>
              </div>
              <span className="queue-count">{tickets.length} total</span>
            </div>
            <div className="ticket-filters">
              <input aria-label="Search tickets" placeholder="Search ticket, customer, subject" value={search} onChange={(event) => setSearch(event.target.value)} />
              <select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">All statuses</option>
                <option>New</option>
                <option>Open</option>
                <option>Pending</option>
                <option>Resolved</option>
              </select>
              <select value={channel} onChange={(event) => setChannel(event.target.value)}>
                <option value="">All channels</option>
                <option value="web">Web</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className="ticket-list">
              {tickets.map((ticket) => (
                <button className={`ticket-row ${selectedId === ticket.TicketId ? 'selected' : ''}`} key={ticket.TicketId} onClick={() => openTicket(ticket.TicketId)}>
                  <div className="ticket-row-top">
                    <strong>{ticket.QuerySubject}</strong>
                    <time>{new Date(ticket.UpdatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</time>
                  </div>
                  <div className="ticket-row-meta">
                    <span>{ticket.CustomerName}</span>
                    <span className={`channel channel-${ticket.Channel}`}>
                      <i />
                      {ticket.Channel}
                    </span>
                    <span className={`status status-${ticket.Status.toLowerCase()}`}>{ticket.Status}</span>
                  </div>
                  <p>{ticket.QueryMessage}</p>
                </button>
              ))}
              {!tickets.length && <div className="empty-state">No tickets match these filters.</div>}
            </div>
          </div>
          <div className={`detail-column ${mobileDetail ? 'mobile-active' : ''}`}>
            {selected ? (
              <>
                <div className="detail-heading">
                  <button type="button" className="mobile-ticket-back" onClick={() => setMobileDetail(false)}>
                    <span className="material-icons">arrow_back</span> Ticket queue
                  </button>
                  <div>
                    <span className="ticket-number">
                      {selected.TicketId} / {selected.Channel}
                    </span>
                    <h2>{selected.QuerySubject}</h2>
                    <p>
                      {selected.CustomerName} · {selected.CustomerEmail}
                      {selected.CustomerPhone ? ` · ${selected.CustomerPhone}` : ''}
                    </p>
                  </div>
                  <div className="ticket-navigation" aria-label="Ticket navigation">
                    <button type="button" onClick={() => navigateTicket(-1)} disabled={tickets.findIndex((ticket) => ticket.TicketId === selectedId) <= 0} aria-label="Previous ticket" title="Previous ticket">
                      <span className="material-icons">chevron_left</span>
                    </button>
                    <span>{tickets.findIndex((ticket) => ticket.TicketId === selectedId) + 1} / {tickets.length}</span>
                    <button type="button" onClick={() => navigateTicket(1)} disabled={tickets.findIndex((ticket) => ticket.TicketId === selectedId) === tickets.length - 1} aria-label="Next ticket" title="Next ticket">
                      <span className="material-icons">chevron_right</span>
                    </button>
                  </div>
                  <div className="status-toolbar">
                    <div className="status-actions">
                      <button
                        type="button"
                        className={`quick-status-btn quick-status-resolved ${selected.Status === 'Resolved' ? 'is-active' : ''}`}
                        onClick={() => updateStatus('Resolved')}
                        disabled={isUpdatingStatus || selected.Status === 'Resolved'}
                      >
                        Resolved
                      </button>
                      <div className={`status-select-wrap ${isUpdatingStatus ? 'saving' : ''}`}>
                        <label className="status-select-label" htmlFor="ticket-status-select">Status</label>
                        <select
                          id="ticket-status-select"
                          className="status-select"
                          value={selected.Status}
                          onChange={(event) => updateStatus(event.target.value)}
                          disabled={isUpdatingStatus}
                          aria-label="Ticket status"
                        >
                          <option>New</option>
                          <option>Open</option>
                          <option>Pending</option>
                          <option>Resolved</option>
                        </select>
                      </div>
                    </div>
                    {isUpdatingStatus && (
                      <span className="status-save-indicator">
                        <span className="material-icons">sync</span>
                        Saving...
                      </span>
                    )}
                  </div>
                </div>
                <div className="conversation" ref={conversationRef}>
                  <div className="message-card customer-message">
                    <div className="message-author">
                      <span className="avatar">{selected.CustomerName?.slice(0, 1).toUpperCase()}</span>
                      <div>
                        <b>{selected.CustomerName}</b>
                        <small>Original query · {new Date(selected.CreatedAt).toLocaleString()}</small>
                      </div>
                    </div>
                    <p>{selected.QueryMessage}</p>
                  </div>
                  {selected.comments?.map((item) => (
                    <div className={`message-card ${/^\[(email|whatsapp)\]\s/i.test(item.Body) ? 'customer-message' : 'admin-message'}`} key={item.Id}>
                      <div className="message-author">
                        <span className="avatar admin-avatar">{item.AuthorName?.slice(0, 1).toUpperCase()}</span>
                        <div>
                          <b>{item.AuthorName}</b>
                          <small>
                            {item.DeliveryStatus === 'Sent' ? `Sent via ${formatDeliveryChannel(item.DeliveryChannel)}` : `${item.DeliveryStatus || 'Pending'} via ${formatDeliveryChannel(item.DeliveryChannel || getReplyChannel(selected))}`} · {new Date(item.CreatedAt).toLocaleString()}
                          </small>
                        </div>
                      </div>
                      <p>{item.Body}</p>
                    </div>
                  ))}
                </div>
                <form className="reply-box" onSubmit={submitComment}>
                  <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Write a reply to the customer..." rows="3" />
                  <div>
                    <span className="reply-hint">
                      <span className="material-icons">history</span>Sent via {getReplyChannel(selected)}
                    </span>
                    <button type="submit" className={`reply-button ${isSendingReply ? 'is-sending' : ''}`} disabled={isSendingReply}>
                      <span className="material-icons">{isSendingReply ? 'hourglass_top' : 'send'}</span>
                      {isSendingReply ? 'Sending...' : 'Send reply'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="empty-detail">
                <span className="material-icons">forum</span>
                <h2>Select a conversation</h2>
                <p>Choose a ticket to review its complete history.</p>
              </div>
            )}
          </div>
        </section>
        {error && <div className="admin-error">{error}</div>}
      </main>
    </div>
  )
}

export default AdminPage
